/**
 * ProcessProceduresTab — Ustawienia → „Procedury SQL" → zakładka „Procesy" (ADR-0074).
 *
 * Katalog wielokrotnego użytku procedur SQL (walidacja / akcja) podpinanych do przejść
 * procesu — analogiczna lista do `DictionariesTab`, ale prostsza: katalog jest jednorodny
 * (brak origin local/global), więc jeden gate `canManage` bramkuje wszystko (podgląd, CRUD,
 * restore) — mirror `useCanManageProcessProcedures` / backend `processes.procedures.manage`.
 *
 * Toggle „Pokaż usunięte" wysyła `include_deleted=true` — backend (walidator + route) zwraca
 * wtedy też soft-skasowane procedury, a przy nich renderuje się przycisk „Przywróć" (undelete).
 * Widok skasowanych i restore bramkuje to samo `processes.procedures.manage`.
 *
 * Konwencja UI (CLAUDE.md §4.12): ZERO window.confirm/alert — ConfirmDialog + useToast.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Pencil, Plus, RotateCcw, Search, Trash2 } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Input } from '@/components/Input';
import { Spinner } from '@/components/Spinner';
import { useToast } from '@/contexts/ToastContext';
import { useCanManageProcessProcedures } from '@/hooks/useCanManageProcessProcedures';
import {
  type ProcessProcedure,
  useCreateProcessProcedure,
  useDeleteProcessProcedure,
  useProcessProceduresList,
  useRestoreProcessProcedure,
  useUpdateProcessProcedure,
} from '@/lib/processes/procedures-api';

import {
  emptyProcessProcedureFormValues,
  ProcessProcedureFormModal,
  type ProcessProcedureFormValues,
} from './ProcessProcedureFormModal';

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export function ProcessProceduresTab() {
  const { t } = useTranslation();
  const toast = useToast();
  const canManage = useCanManageProcessProcedures();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [showDeleted, setShowDeleted] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<ProcessProcedure | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ProcessProcedure | null>(null);
  // Błąd 409 (nazwa procedury zajęta) z ostatniej próby zapisu — pokazywany inline w
  // modalu (§4.12), reset przy każdym otwarciu/zamknięciu i tuż przed próbą zapisu.
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

  const listQ = useProcessProceduresList({ q: debouncedSearch, includeDeleted: showDeleted });
  const createMutation = useCreateProcessProcedure();
  const updateMutation = useUpdateProcessProcedure();
  const deleteMutation = useDeleteProcessProcedure();
  const restoreMutation = useRestoreProcessProcedure();

  if (!canManage) {
    return (
      <Card padding="md" data-testid="process-procedures-tab-no-permission">
        <p className="text-sm text-text-tertiary">
          {t('tenant.processProcedures.list.noPermission')}
        </p>
      </Card>
    );
  }

  const formInitialValues: ProcessProcedureFormValues = editing
    ? {
        name: editing.name,
        description: editing.description ?? '',
        kind: editing.kind,
        sql: editing.sql,
        declaredParams: editing.declaredParams,
      }
    : emptyProcessProcedureFormValues();

  function handleCreate(values: ProcessProcedureFormValues) {
    setServerErrors({});
    createMutation.mutate(
      {
        name: values.name,
        description: values.description.trim() || null,
        kind: values.kind,
        sql: values.sql,
        declaredParams: values.declaredParams,
      },
      {
        onSuccess: () => {
          toast.show({ variant: 'success', title: t('tenant.processProcedures.toast.created') });
          setCreateOpen(false);
        },
        onError: (err) => {
          const code = isAxiosError(err) ? (err.response?.data?.error as string | undefined) : null;
          if (code === 'NAME_TAKEN') {
            toast.show({
              variant: 'warning',
              title: t('tenant.processProcedures.toast.nameTaken'),
            });
            setServerErrors({ name: t('tenant.processProcedures.toast.nameTaken') });
            return;
          }
          toast.show({ variant: 'error', title: t('tenant.processProcedures.toast.createError') });
        },
      },
    );
  }

  function handleUpdate(id: string, values: ProcessProcedureFormValues) {
    setServerErrors({});
    updateMutation.mutate(
      {
        id,
        values: {
          name: values.name,
          description: values.description.trim() || null,
          sql: values.sql,
          declaredParams: values.declaredParams,
        },
      },
      {
        onSuccess: () => {
          toast.show({ variant: 'success', title: t('tenant.processProcedures.toast.updated') });
          setEditing(null);
        },
        onError: (err) => {
          const code = isAxiosError(err) ? (err.response?.data?.error as string | undefined) : null;
          if (code === 'NAME_TAKEN') {
            toast.show({
              variant: 'warning',
              title: t('tenant.processProcedures.toast.nameTaken'),
            });
            setServerErrors({ name: t('tenant.processProcedures.toast.nameTaken') });
            return;
          }
          toast.show({ variant: 'error', title: t('tenant.processProcedures.toast.updateError') });
        },
      },
    );
  }

  function handleDelete(procedure: ProcessProcedure) {
    deleteMutation.mutate(procedure.id, {
      onSuccess: () => {
        toast.show({ variant: 'success', title: t('tenant.processProcedures.toast.deleted') });
        setPendingDelete(null);
      },
      onError: (err) => {
        const code = isAxiosError(err) ? (err.response?.data?.error as string | undefined) : null;
        if (code === 'PROCEDURE_IN_USE') {
          toast.show({ variant: 'warning', title: t('tenant.processProcedures.toast.inUse') });
          setPendingDelete(null);
          return;
        }
        toast.show({ variant: 'error', title: t('tenant.processProcedures.toast.deleteError') });
        setPendingDelete(null);
      },
    });
  }

  function handleRestore(procedure: ProcessProcedure) {
    restoreMutation.mutate(procedure.id, {
      onSuccess: () => {
        toast.show({ variant: 'success', title: t('tenant.processProcedures.toast.restored') });
      },
      onError: () => {
        toast.show({ variant: 'error', title: t('tenant.processProcedures.toast.restoreError') });
      },
    });
  }

  const procedures = listQ.data ?? [];

  return (
    <div data-testid="process-procedures-tab">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">
            {t('tenant.processProcedures.list.title')}
          </h2>
          <p className="text-xs text-text-tertiary">
            {t('tenant.processProcedures.list.subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('tenant.processProcedures.list.searchPlaceholder')}
            prefixIcon={<Search className="h-4 w-4" />}
            data-testid="process-procedures-search"
          />
          <label className="flex items-center gap-1.5 text-xs text-text-secondary">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
              data-testid="process-procedures-show-deleted"
            />
            {t('tenant.processProcedures.list.showDeleted')}
          </label>
          <Button
            variant="primary"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => {
              setServerErrors({});
              setCreateOpen(true);
            }}
            data-testid="process-procedures-new-btn"
          >
            {t('tenant.processProcedures.list.newProcedure')}
          </Button>
        </div>
      </header>

      {listQ.isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Spinner />
        </div>
      ) : procedures.length === 0 ? (
        <Card padding="md">
          <p className="text-sm text-text-tertiary">{t('tenant.processProcedures.list.empty')}</p>
        </Card>
      ) : (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border-primary bg-bg-secondary text-xs uppercase tracking-wider text-text-muted">
                <tr>
                  <th className="px-4 py-2 text-left">
                    {t('tenant.processProcedures.list.col.name')}
                  </th>
                  <th className="px-4 py-2 text-left">
                    {t('tenant.processProcedures.list.col.kind')}
                  </th>
                  <th className="px-4 py-2 text-left">
                    {t('tenant.processProcedures.list.col.params')}
                  </th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-primary">
                {procedures.map((procedure) => {
                  const isDeleted = procedure.gcrecord != null;
                  return (
                    <tr key={procedure.id} data-testid={`process-procedure-row-${procedure.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-text-primary">{procedure.name}</div>
                        {procedure.description && (
                          <div className="text-xs text-text-tertiary">{procedure.description}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge color={procedure.kind === 'validation' ? 'blue' : 'amber'}>
                          {t(`tenant.processProcedures.list.kind.${procedure.kind}`)}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-text-secondary">
                        {procedure.declaredParams.length}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {isDeleted ? (
                            <button
                              type="button"
                              onClick={() => handleRestore(procedure)}
                              aria-label={t('tenant.processProcedures.list.restore')}
                              className="rounded p-1.5 text-text-muted transition hover:bg-bg-tertiary hover:text-text-primary"
                              data-testid={`process-procedure-restore-${procedure.id}`}
                            >
                              <RotateCcw className="h-3.5 w-3.5" />
                            </button>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  setServerErrors({});
                                  setEditing(procedure);
                                }}
                                aria-label={t('common.edit')}
                                className="rounded p-1.5 text-text-muted transition hover:bg-bg-tertiary hover:text-text-primary"
                                data-testid={`process-procedure-edit-${procedure.id}`}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => setPendingDelete(procedure)}
                                aria-label={t('common.delete')}
                                className="rounded p-1.5 text-text-muted transition hover:bg-error-500/10 hover:text-error-500"
                                data-testid={`process-procedure-delete-${procedure.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <ProcessProcedureFormModal
        open={createOpen || editing !== null}
        mode={editing ? 'edit' : 'create'}
        initialValues={formInitialValues}
        serverErrors={serverErrors}
        saving={createMutation.isPending || updateMutation.isPending}
        onSubmit={(values) => {
          if (editing) {
            handleUpdate(editing.id, values);
          } else {
            handleCreate(values);
          }
        }}
        onClose={() => {
          setCreateOpen(false);
          setEditing(null);
          setServerErrors({});
        }}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('tenant.processProcedures.confirmDelete.title')}
        message={
          pendingDelete
            ? t('tenant.processProcedures.confirmDelete.message', { name: pendingDelete.name })
            : ''
        }
        confirmLabel={t('common.delete')}
        variant="danger"
        onConfirm={() => {
          if (pendingDelete) handleDelete(pendingDelete);
        }}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}
